'use client';

import {useEffect, useState} from 'react';
import {canReloadForChunkLoadError, reloadForChunkLoadError} from '@/lib/chunk-load-error';

/**
 * Für Error-Boundaries (`error.tsx`, `global-error.tsx`): Ist `error` ein
 * Chunk-Ladefehler, wird einmalig ein harter Reload mit Cache-Buster
 * ausgelöst (siehe chunk-load-error.ts). Liefert `true`, solange der Reload
 * läuft — die Boundary rendert dann nichts und meldet nichts. Ist das
 * Fenster von einer Minute pro Pfad bereits verbraucht oder ist dieses
 * Dokument selbst schon der Reload (URL trägt `mg_reload`), liefert der Hook
 * sofort `false`, und die Boundary zeigt ihre normale Fehlerseite. Für alle
 * anderen Fehler liefert er ebenfalls sofort `false`.
 *
 * Die Entscheidung fällt einmal pro Mount (Lazy-Initializer). Das reicht,
 * weil Nexts `ErrorBoundaryHandler` die Fehlerkomponente pro Fehler neu
 * mountet: `render()` liefert bei `state.error` die Fehlerkomponente, sonst
 * `children`; nach `reset()` oder einem Pfadwechsel wird sie also abgebaut
 * und beim nächsten Fehler frisch aufgebaut
 * (node_modules/next/dist/client/components/error-boundary.js).
 *
 * Der Effekt führt die Navigation aus. Lehnt `reloadForChunkLoadError` sie
 * ab, obwohl der Initializer sie noch erlaubt hatte (zwischen Render und
 * Effekt hat ein früherer Effekt — z. B. die Cleanup-Komponente im
 * Root-Layout — den Versuch für diesen Pfad schon markiert), fällt der Hook
 * einmalig auf `false` zurück, damit nicht dauerhaft eine leere Fläche steht.
 */
export function useChunkLoadRecovery(error: unknown): boolean {
    const [reloading, setReloading] = useState(() => canReloadForChunkLoadError(error));

    useEffect(() => {
        if (!reloading) return;
        if (!reloadForChunkLoadError(error)) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- Rückfall auf die Fehlerseite, wenn der externe Seiteneffekt (Navigation) verweigert wurde; läuft höchstens einmal, da reloading danach false ist.
            setReloading(false);
        }
    }, [reloading, error]);

    return reloading;
}
